import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  EMAIL_CLIENT_DELETE_ACCOUNT_ENDPOINT,
  EMAIL_CLIENT_GET_ACCOUNTS_ENDPOINT,
  EMAIL_CLIENT_GET_BUSINESS_DOCUMENT_MESSAGES_ENDPOINT,
  EMAIL_CLIENT_GET_MESSAGE_ENDPOINT,
  EMAIL_CLIENT_GET_MESSAGES_ENDPOINT,
  EMAIL_CLIENT_GET_UNREAD_COUNT_ENDPOINT,
  EMAIL_CLIENT_PATCH_MESSAGE_ENDPOINT,
  EMAIL_CLIENT_POST_ACCOUNT_ENDPOINT,
  EMAIL_CLIENT_POST_ACCOUNT_DISCOVERY_ENDPOINT,
  EMAIL_CLIENT_POST_ACCOUNT_OAUTH_START_ENDPOINT,
  EMAIL_CLIENT_POST_ACCOUNT_REFRESH_ENDPOINT,
  EMAIL_CLIENT_POST_ACCOUNT_TEST_ENDPOINT,
  EMAIL_CLIENT_POST_MESSAGE_ENDPOINT,
  EMAIL_CLIENT_PUT_ACCOUNT_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { formatString } from '../utils/string.utils';
import {
  EmailAccount,
  EmailAccountDiscovery,
  EmailAccountDiscoveryInput,
  EmailAccountInput,
  EmailMailbox,
  EmailMessage,
  EmailMessagePage,
  EmailOAuthStartResponse,
  EmailSendInput,
} from './email-client.types';

@Injectable({
  providedIn: 'root',
})
export class EmailClientService {
  constructor(private readonly restApi: RestApiService) {}

  listAccounts(): Observable<EmailAccount[]> {
    return this.restApi.get<EmailAccount[]>(EMAIL_CLIENT_GET_ACCOUNTS_ENDPOINT);
  }

  discoverAccount(input: EmailAccountDiscoveryInput): Observable<EmailAccountDiscovery> {
    return this.restApi.post<EmailAccountDiscovery>(EMAIL_CLIENT_POST_ACCOUNT_DISCOVERY_ENDPOINT, input);
  }

  createAccount(input: EmailAccountInput): Observable<EmailAccount> {
    return this.restApi.post<EmailAccount>(EMAIL_CLIENT_POST_ACCOUNT_ENDPOINT, input);
  }

  updateAccount(id: string, input: EmailAccountInput): Observable<EmailAccount> {
    return this.restApi.put<EmailAccount>(formatString(EMAIL_CLIENT_PUT_ACCOUNT_ENDPOINT, id), input);
  }

  deleteAccount(id: string): Observable<void> {
    return this.restApi.delete<void>(formatString(EMAIL_CLIENT_DELETE_ACCOUNT_ENDPOINT, id));
  }

  testAccount(id: string): Observable<{ message: string }> {
    return this.restApi.post<{ message: string }>(
      formatString(EMAIL_CLIENT_POST_ACCOUNT_TEST_ENDPOINT, id),
      {},
    );
  }

  refreshAccount(id: string): Observable<{ message: string }> {
    return this.restApi.post<{ message: string }>(
      formatString(EMAIL_CLIENT_POST_ACCOUNT_REFRESH_ENDPOINT, id),
      {},
    );
  }

  startOAuth(id: string): Observable<EmailOAuthStartResponse> {
    return this.restApi.post<EmailOAuthStartResponse>(
      formatString(EMAIL_CLIENT_POST_ACCOUNT_OAUTH_START_ENDPOINT, id),
      {},
    );
  }

  listMessages(query: {
    account_id: string;
    mailbox: EmailMailbox;
    page: number;
    limit: number;
    search?: string;
  }): Observable<EmailMessagePage> {
    let params = new HttpParams()
      .set('account_id', query.account_id)
      .set('mailbox', query.mailbox)
      .set('page', query.page)
      .set('limit', query.limit);
    const search = query.search?.trim();
    if (search) params = params.set('search', search);
    return this.restApi.get<EmailMessagePage>(EMAIL_CLIENT_GET_MESSAGES_ENDPOINT, { params });
  }

  unreadCount(): Observable<{ unread_count: number }> {
    return this.restApi.get<{ unread_count: number }>(EMAIL_CLIENT_GET_UNREAD_COUNT_ENDPOINT);
  }

  getMessage(id: string): Observable<EmailMessage> {
    return this.restApi.get<EmailMessage>(formatString(EMAIL_CLIENT_GET_MESSAGE_ENDPOINT, id));
  }

  updateMessage(id: string, input: { is_read?: boolean; is_starred?: boolean; mailbox?: 'inbox' | 'archive' | 'trash' }): Observable<EmailMessage> {
    return this.restApi.patch<EmailMessage>(formatString(EMAIL_CLIENT_PATCH_MESSAGE_ENDPOINT, id), input);
  }

  sendMessage(input: EmailSendInput): Observable<EmailMessage> {
    return this.restApi.post<EmailMessage>(EMAIL_CLIENT_POST_MESSAGE_ENDPOINT, input);
  }

  businessDocumentMessages(id: string): Observable<EmailMessage[]> {
    return this.restApi.get<EmailMessage[]>(
      formatString(EMAIL_CLIENT_GET_BUSINESS_DOCUMENT_MESSAGES_ENDPOINT, id),
    );
  }
}
